/**
 * Tabs — accessible tab panel built on Radix UI.
 *
 * Keyboard navigation (Arrow / Home / End), focus management, and
 * ARIA tabpanel semantics come from Radix. Visual treatment is
 * tokens-driven via Tailwind utilities.
 *
 * Usage:
 *
 *     <Tabs defaultValue="proof">
 *       <TabsList>
 *         <TabsTrigger value="proof">Proof</TabsTrigger>
 *         <TabsTrigger value="counterexample">Counterexample</TabsTrigger>
 *       </TabsList>
 *       <TabsContent value="proof">…</TabsContent>
 *       <TabsContent value="counterexample">…</TabsContent>
 *     </Tabs>
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "../../styling/index.js";

export const Tabs = TabsPrimitive.Root;

export type TabsListProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>;
export type TabsTriggerProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>;
export type TabsContentProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>;

export const TabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  TabsListProps
>(function TabsList({ className, ...props }, ref) {
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-neutral-100 p-1",
        className,
      )}
      {...props}
    />
  );
});

export const TabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(function TabsTrigger({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center rounded px-3 py-1 text-xs font-medium text-neutral-700",
        "transition-colors hover:text-neutral-900",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        "data-[state=active]:bg-white data-[state=active]:text-neutral-900 data-[state=active]:shadow-sm",
        className,
      )}
      {...props}
    />
  );
});

export const TabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(function TabsContent({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn("mt-3 focus-visible:outline-none", className)}
      {...props}
    />
  );
});
